import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import CountUp from 'react-countup';

const GlobalAnimatedProgressBar = ({
    percentage = 0,
    maxPercentage = 100,
    label,
    value,
    total,
    colorScheme = 'default',
    customColor,
    height = 24,
    showShineEffect = true,
    animationDuration = 1.2,
    animationDelay = 0,
    countUpDuration = 1.2,
    countUpDelay = 0.3,
    showLabel = true,
    showPercentage = true,
    showCounts = false,
    borderRadius = 1,
    variant = 'filled',
    size = 'medium',
    triggerAnimation = true,
    textPosition = 'right',
    backgroundColor = 'grey.100',
    textSize = 'auto',
}) => {
    const [animateValue, setAnimateValue] = useState(0);
    const isInitialized = useRef(false);

    // Handle size variants
    const sizeConfig = {
        small: { height: 16, borderRadius: 0.5 },
        medium: { height: 24, borderRadius: 1 },
        large: { height: 32, borderRadius: 1.5 },
    };

    const currentSize = sizeConfig[size] || sizeConfig.medium;
    const barHeight = height || currentSize.height;
    const barBorderRadius = borderRadius || currentSize.borderRadius;

    // Determine text variant and custom styling
    const getTextVariant = () => {
        if (textSize === 'auto') {
            return size === 'small' ? 'caption' : size === 'large' ? 'body1' : 'body2';
        }
        if (['caption', 'body2', 'body1', 'h6', 'subtitle1', 'subtitle2'].includes(textSize)) {
            return textSize;
        }
        return 'body2';
    };

    const getTextStyles = () => {
        const baseStyles = {};

        if (textSize !== 'auto' && !['caption', 'body2', 'body1', 'h6', 'subtitle1', 'subtitle2'].includes(textSize)) {
            baseStyles.fontSize = textSize;
        }

        return baseStyles;
    };

    const getGradientColor = (percent) => {
        const green = '#33BD95';        // Green
        const darkGreen = '#1A6B4F';    // Darker green
        const amber = '#FF9f07';        // Amber
        const darkAmber = '#CC8B2A';    // Darker amber
        const red = '#FF6d54';          // Red
        const darkRed = '#B71C1C';      // Dark Red

        if (percent <= 5) {
            // 0-5%: Gradient from dark green to light green
            const ratio = percent / 5; // 0 to 1
            return `linear-gradient(90deg, ${interpolateColor(darkGreen, green, ratio)} 0%, ${green} 100%)`;
        } else if (percent <= 15) {
            // 5-15%: Gradient from dark amber to light amber
            const ratio = (percent - 5) / 10; // 0 to 1
            return `linear-gradient(90deg, ${interpolateColor(darkAmber, amber, ratio)} 0%, ${amber} 100%)`;
        } else {
            // 15%+: Gradient from dark red to light red
            const ratio = Math.min((percent - 15) / 85, 1);
            return `linear-gradient(90deg, ${interpolateColor(darkRed, red, ratio)} 0%, ${red} 100%)`;
        }
    };

    // Helper function to interpolate between two hex colors
    const interpolateColor = (color1, color2, ratio) => {
        // Convert hex to RGB
        const hexToRgb = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null;
        };

        // Convert RGB to hex
        const rgbToHex = (r, g, b) => {
            return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        };

        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        if (!rgb1 || !rgb2) return color1;

        const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * ratio);
        const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * ratio);
        const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * ratio);

        return rgbToHex(r, g, b);
    };

    // Color scheme mapping
    const getBarColor = () => {
        if (customColor) return customColor;

        switch (colorScheme) {
            case 'gradient':
                return getGradientColor(percentage);
            case 'success':
                return 'success.main';
            case 'warning':
                return 'warning.main';
            case 'error':
                return 'error.main';
            case 'primary':
                return 'primary.main';
            case 'secondary':
                return 'secondary.main';
            case 'thresholds':
                if (percentage >= 80) return 'success.main';
                if (percentage >= 50) return 'warning.main';
                return 'error.main';
            case 'reverseThresholds':
                if (percentage >= 90) return 'error.main';
                if (percentage >= 50) return 'warning.main';
                return 'success.main';
            default:
                return 'primary.main';
        }
    };

    // Animation effect to smoothly transition the progress value
    useEffect(() => {
        if (triggerAnimation) {
            const timer = setTimeout(() => {
                setAnimateValue(percentage);
                if (!isInitialized.current) {
                    isInitialized.current = true;
                }
            }, 150 + animationDelay);
            return () => clearTimeout(timer);
        } else {
            setAnimateValue(percentage);
        }
    }, [triggerAnimation, percentage, animationDelay]);

    // Calculate actual value percentage (normalized against maxPercentage)
    const normalizedValue = maxPercentage > 0 ? (animateValue / maxPercentage) * 100 : 0;
    const displayValue = Math.min(Math.max(normalizedValue, 0), 100);

    const barColor = getBarColor();
    const isGradient = typeof barColor === 'string' && barColor.includes('linear-gradient');

    return (
        <Box>
            {/* Label and Value Row */}
            {(showLabel || showPercentage || showCounts) && textPosition !== 'none' && textPosition !== 'inside' && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}>
                    {showLabel && label && (
                        <Typography variant="body2" fontWeight="medium">
                            {label}
                        </Typography>
                    )}
                    {(showPercentage || showCounts) && (
                        <Typography
                            variant={getTextVariant()}
                            fontWeight="medium"
                            sx={getTextStyles()}
                        >
                            {showCounts && value !== undefined && total !== undefined && (
                                <>
                                    {triggerAnimation ? (
                                        <CountUp
                                            start={0}
                                            end={value}
                                            duration={countUpDuration}
                                            delay={animationDelay + countUpDelay}
                                            preserveValue
                                        />
                                    ) : value}
                                    {' of '}
                                    {triggerAnimation ? (
                                        <CountUp
                                            start={0}
                                            end={total}
                                            duration={countUpDuration}
                                            delay={animationDelay + countUpDelay}
                                            preserveValue
                                        />
                                    ) : total}
                                    {showPercentage && ' ('}
                                </>
                            )}
                            {showPercentage && (
                                <>
                                    {triggerAnimation ? (
                                        <CountUp
                                            start={0}
                                            end={percentage}
                                            duration={countUpDuration}
                                            delay={animationDelay + countUpDelay}
                                            preserveValue
                                        />
                                    ) : percentage}
                                    %
                                </>
                            )}
                            {showCounts && showPercentage && ')'}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Progress Bar Container */}
            <Box sx={{ position: 'relative' }}>
                {/* MUI LinearProgress with custom styling */}
                <LinearProgress
                    variant="determinate"
                    value={displayValue}
                    sx={{
                        height: barHeight,
                        borderRadius: barBorderRadius,
                        backgroundColor: backgroundColor,
                        ...(variant === 'outlined' && {
                            border: '1px solid',
                            borderColor: 'divider',
                        }),
                        '& .MuiLinearProgress-bar': {
                            borderRadius: barBorderRadius,
                            transition: `transform ${animationDuration}s cubic-bezier(0.4, 0, 0.2, 1)`,
                            transitionDelay: isInitialized.current ? '0s' : `${animationDelay}s`,
                            ...(isGradient ? {
                                background: barColor,
                            } : {
                                backgroundColor: barColor,
                            }),
                        },
                        // Shine effect overlay
                        overflow: 'hidden', // This is key - contains the shine effect
                        ...(showShineEffect && {
                            '& .MuiLinearProgress-bar::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                animation: showShineEffect && animateValue > 0 ? 'shine 2s ease-in-out' : 'none',
                                animationDelay: `${animationDelay + 0.5}s`,
                                borderRadius: barBorderRadius,
                            },
                            '@keyframes shine': {
                                '0%': { left: '-100%' },
                                '100%': { left: '100%' },
                            },
                        }),
                    }}
                />

                {/* Inside text overlay */}
                {textPosition === 'inside' && (showPercentage || showCounts) && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}>
                        <Typography
                            variant={textSize === 'auto' ? (size === 'small' ? 'caption' : 'body2') : getTextVariant()}
                            fontWeight="bold"
                            sx={{
                                color: 'white',
                                textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                ...(textSize !== 'auto' && !['caption', 'body2', 'body1', 'h6', 'subtitle1', 'subtitle2'].includes(textSize) && {
                                    fontSize: textSize,
                                }),
                            }}
                        >
                            {showCounts && value !== undefined && total !== undefined && (
                                <>
                                    {triggerAnimation ? (
                                        <CountUp
                                            start={0}
                                            end={value}
                                            duration={countUpDuration}
                                            delay={animationDelay + countUpDelay}
                                            preserveValue
                                        />
                                    ) : value}
                                    /{total}
                                    {showPercentage && ' ('}
                                </>
                            )}
                            {showPercentage && (
                                <>
                                    {triggerAnimation ? (
                                        <CountUp
                                            start={0}
                                            end={percentage}
                                            duration={countUpDuration}
                                            delay={animationDelay + countUpDelay}
                                            preserveValue
                                        />
                                    ) : percentage}
                                    %
                                </>
                            )}
                            {showCounts && showPercentage && ')'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default GlobalAnimatedProgressBar;